import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/react-use-window-sessionstorage/react-use-window-sessionstorage/node_modules/gatsby-theme-docz/src/base/Layout.js";
import { UseSessionStorageNumberExample } from '../__docz__/components/UseSessionStorageNumberExample';
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">



    <h1>useSessionStorageNumber</h1>
    <h2 {...{
      "id": "description"
    }}>{`Description`}</h2>
    <p>{`This hook gets and sets a `}<inlineCode parentName="p">{`number`}</inlineCode>{` in `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/API/Window/sessionStorage"
      }}>{`sessionStorage`}</a>{`. `}</p>
    <p>{`Features synchronization across hooks sharing the same key name.`}</p>
    <h2 {...{
      "id": "usage"
    }}>{`Usage`}</h2>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { useSessionStorageNumber } from 'react-use-window-sessionstorage';
`}</code></pre>
    <p>{`In your function component:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const [value, setValue] = useSessionStorageNumber('pi', 3.14159);
`}</code></pre>
    <p>{`The hook takes a key name and an optional default value argument. The default value will be immediately written to sessionStorage and returned if no value is present already. Uses `}<inlineCode parentName="p">{`null`}</inlineCode>{` when no default value is provided.`}</p>
    <p>{`Use `}<inlineCode parentName="p">{`value`}</inlineCode>{` and `}<inlineCode parentName="p">{`setValue`}</inlineCode>{` to read and write the value.`}</p>
    <p>{`Because sessionStorage accepts strings only, the value is encoded using
`}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/stringify"
      }}><inlineCode parentName="a">{`JSON.stringify()`}</inlineCode></a>{`
and decoded using `}<a parentName="p" {...{
        "href": "https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/JSON/parse"
      }}><inlineCode parentName="a">{`JSON.parse()`}</inlineCode></a>{`.`}</p>
    <h3 {...{
      "id": "additional-features"
    }}>{`Additional Features`}</h3>
    <p>{`The hook provides additional features in its return array, should you be interested:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`const [value, setValue, loading, available, reset] = useSessionStorageNumber('pi', 3.14159);
`}</code></pre>
    <ul>
      <li parentName="ul">{`A `}<inlineCode parentName="li">{`loading`}</inlineCode>{` value of `}<inlineCode parentName="li">{`true`}</inlineCode>{` indicates that the value is being loaded from sessionStorage.`}</li>
      <li parentName="ul">{`An `}<inlineCode parentName="li">{`available`}</inlineCode>{` value of `}<inlineCode parentName="li">{`true`}</inlineCode>{` indicates that `}<inlineCode parentName="li">{`sessionStorage`}</inlineCode>{` is supported and available for use. `}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`reset()`}</inlineCode>{` function sets the value back to the provided default, or `}<inlineCode parentName="li">{`null`}</inlineCode>{` if none was given.`}</li>
    </ul>
    <h2 {...{
      "id": "signature"
    }}>{`Signature`}</h2>
    <p><inlineCode parentName="p">{`useSessionStorageNumber(keyName: string, defaultValue?: number | null) => [number | null, (value: number | null) => void, boolean, () => void, () => void]`}</inlineCode></p>
    <h3 {...{
      "id": "props"
    }}>{`Props`}</h3>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Prop`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`keyName`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`string`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><strong parentName="td">{`Required.`}</strong>{` Key name to use in sessionStorage.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`defaultValue`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Optional. Provide a default `}<inlineCode parentName="td">{`number`}</inlineCode>{` value when the key's value is not found in sessionStorage. Will be immediately written to sessionStorage if not present. Use `}<inlineCode parentName="td">{`null`}</inlineCode>{` for no default.`}</td>
        </tr>
      </tbody>
    </table>
    <h3 {...{
      "id": "return"
    }}>{`Return`}</h3>
    <p>{`The hook returns an array containing:`}</p>
    <table>
      <thead parentName="table">
        <tr parentName="thead">
          <th parentName="tr" {...{
            "align": null
          }}>{`Index`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Name`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Type`}</th>
          <th parentName="tr" {...{
            "align": null
          }}>{`Description`}</th>
        </tr>
      </thead>
      <tbody parentName="table">
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`0`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`value`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`number`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`null`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`The `}<inlineCode parentName="td">{`number`}</inlineCode>{` value from sessionStorage, when `}<inlineCode parentName="td">{`loading`}</inlineCode>{` is `}<inlineCode parentName="td">{`false`}</inlineCode>{`. Will be the default value (or `}<inlineCode parentName="td">{`null`}</inlineCode>{` if none provided) while loading is `}<inlineCode parentName="td">{`true`}</inlineCode>{`. If `}<inlineCode parentName="td">{`null`}</inlineCode>{` when not loading, the key was not found in sessionStorage.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`1`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`setValue`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`(value: number`}</inlineCode>{` `}{`|`}{` `}<inlineCode parentName="td">{`null) => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Sets the value in sessionStorage. Provide `}<inlineCode parentName="td">{`null`}</inlineCode>{` to set it back to the default value. Providing `}<inlineCode parentName="td">{`null`}</inlineCode>{` will delete the key from sessionStorage if the default value is `}<inlineCode parentName="td">{`null`}</inlineCode>{`.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`2`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`loading`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode>{` while the value loads from sessionStorage, `}<inlineCode parentName="td">{`false`}</inlineCode>{` otherwise.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`3`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`available`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`boolean`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`true`}</inlineCode>{` if sessionStorage is supported and available for use, `}<inlineCode parentName="td">{`false`}</inlineCode>{` otherwise.`}</td>
        </tr>
        <tr parentName="tbody">
          <td parentName="tr" {...{
            "align": null
          }}>{`4`}</td>
          <td parentName="tr" {...{
            "align": null
          }}>{`reset`}</td>
          <td parentName="tr" {...{
            "align": null
          }}><inlineCode parentName="td">{`() => void`}</inlineCode></td>
          <td parentName="tr" {...{
            "align": null
          }}>{`Reset the value to the default.`}</td>
        </tr>
      </tbody>
    </table>
    <h2 {...{
      "id": "example"
    }}>{`Example`}</h2>
    <div style={{
      border: 'solid 2px #67788a',
      padding: '20px'
    }}>
  <UseSessionStorageNumberExample mdxType="UseSessionStorageNumberExample" />
    </div>
    <pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import * as React from 'react';
import { useSessionStorageNumber } from 'react-use-window-sessionstorage';

export function UseSessionStorageNumberExample() {
  const defaultValue = 3.14159;
  const [enteredValue, setEnteredValue] = React.useState('');
  const [value, setValue, loading, available, reset] = useSessionStorageNumber('numValue', defaultValue);
  const [value2, setValue2, loading2] = useSessionStorageNumber('numValue', defaultValue);

  return (
    <div>
      {!loading && (
        <div>
          <div>
            Key "numValue":{' '}
            <input
              type="text"
              value={enteredValue}
              onChange={e => setEnteredValue(e.target.value)}
              onKeyPress={e => e.key === 'Enter' && setValue(parseFloat(enteredValue))}
            />
            <button onClick={() => setValue(parseFloat(enteredValue))}>Set</button>
          </div>
          <div>
            <strong>Stored value:</strong> {value}
          </div>
        </div>
      )}
      <div>
        <button onClick={() => reset()}>Reset "numValue" to default</button>
      </div>
      <hr />
      {!loading2 && <div>Another "numValue" hook (different hook, will synchronize): {value2}</div>}
    </div>
  );
}
`}</code></pre>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      